import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getNotificationPreferences } from "../../api/notificationPreferencesApi";
import { listFavoriteListings, removeFavoriteListing } from "../../api/favoritesApi";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import { formatCanonicalPriceRange } from "../../utils/canonicalListings";
import { openPropertyDetail } from "../../utils/openPropertyDetail";

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [listingMode, setListingMode] = useState("");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [favoriteAlertsEnabled, setFavoriteAlertsEnabled] = useState(null);

  const loadFavorites = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await listFavoriteListings({
        listingMode: listingMode || undefined,
        q: search.trim() || undefined,
        pageSize: 100,
      });
      setFavorites(body.data || []);
    } catch (error) {
      setFavorites([]);
      setErrorMessage(error.message || "Unable to load favorites.");
    } finally {
      setIsLoading(false);
    }
  }, [listingMode, search]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const body = await getNotificationPreferences();
        if (!cancelled) {
          const prefs = body.preferences || {};
          setFavoriteAlertsEnabled(
            Boolean(prefs.emailNotifications && prefs.favoriteListingAlerts)
          );
        }
      } catch {
        if (!cancelled) {
          setFavoriteAlertsEnabled(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRemove = async (favoriteListingId) => {
    try {
      await removeFavoriteListing(favoriteListingId);
      await loadFavorites();
    } catch (error) {
      setErrorMessage(error.message || "Unable to remove favorite.");
    }
  };

  return (
    <section className="panel-card">
      <div className="account-security-section__header">
        <h2>Favorite listings</h2>
        <Link to="/">Browse map</Link>
      </div>

      {favoriteAlertsEnabled === true ? (
        <p className="muted">
          You&apos;ll receive alerts when favorite listings change. Manage settings on{" "}
          <Link to="/account/notifications">Notifications</Link>.
        </p>
      ) : null}
      {favoriteAlertsEnabled === false ? (
        <p className="muted">
          Favorite listing alerts are off.{" "}
          <Link to="/account/notifications">Enable notifications</Link>.
        </p>
      ) : null}

      <div className="admin-users-toolbar">
        <input
          type="search"
          placeholder="Search favorites"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={listingMode} onChange={(event) => setListingMode(event.target.value)}>
          <option value="">All modes</option>
          <option value="buy">Buy</option>
          <option value="rent">Rent</option>
        </select>
        <button type="button" className="midpoint-share-button" onClick={loadFavorites}>
          Refresh
        </button>
      </div>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
      {isLoading ? <p className="muted">Loading…</p> : null}

      {!isLoading && favorites.length ? (
        <div className="account-favorites-grid">
          {favorites.map((favorite) => (
            <article key={favorite.favoriteListingId} className="account-favorite-card">
              {favorite.imageUrl ? (
                <img src={favorite.imageUrl} alt="" className="account-favorite-card__image" />
              ) : (
                <div className="account-favorite-card__image account-favorite-card__image--empty" />
              )}
              <div className="account-favorite-card__body">
                <StatusBadge
                  label={
                    favorite.status === "active"
                      ? "Active"
                      : favorite.status === "unavailable"
                        ? "No longer available"
                        : "Unknown"
                  }
                  tone={favorite.status === "active" ? "confirmed" : "disputed"}
                />
                <h3>{favorite.title || favorite.addressLabel || "Listing"}</h3>
                <p className="muted">{favorite.addressLabel}</p>
                <p>
                  {formatCanonicalPriceRange({
                    priceMin: favorite.price,
                    priceMax: favorite.priceMax,
                    currencyCode: favorite.currency,
                  })}
                </p>
                {favorite.status !== "active" ? (
                  <p className="muted">This listing may no longer be available.</p>
                ) : null}
                <p className="muted">Saved {formatDateTime(favorite.createdAt)}</p>
                <div className="account-favorite-card__actions">
                  {favorite.canonicalListingId ? (
                    <button
                      type="button"
                      className="midpoint-share-button"
                      onClick={() =>
                        openPropertyDetail(navigate, {
                          id: favorite.canonicalListingId,
                          canonicalListingId: favorite.canonicalListingId,
                        })
                      }
                    >
                      View details
                    </button>
                  ) : null}
                  <Link to="/" className="midpoint-share-button">
                    Open map
                  </Link>
                  <button
                    type="button"
                    className="midpoint-share-button"
                    onClick={() => handleRemove(favorite.favoriteListingId)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {!isLoading && !favorites.length ? (
        <p className="muted">No favorites yet. Save listings from the map search.</p>
      ) : null}
    </section>
  );
};

export default FavoritesPage;
